import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import apiCall from "auth/ApiCallInstance"; // your axios instance
import { useSelector } from "react-redux";

const Ctx = createContext(null);
export const useAdminNotifications = () => useContext(Ctx);

// Polling interval in milliseconds (30 seconds)
const POLL_INTERVAL = 30000;

export function AdminNotificationsProvider({ children }) {
  const [items, setItems] = useState([]);
  const pollIntervalRef = useRef(null);
  const lastFetchRef = useRef(0);
  
  // Get the current authenticated user from Redux store
  const authUser = useSelector(state => state.auth?.user);
  const authToken = useSelector(state => state.auth?.token);

  const fetchLatest = useCallback(async (silent = false) => {
    if (!authToken) {
      // Not logged in, clear notifications
      setItems([]);
      return;
    }
    try {
      if (!silent) {
        console.log("[Notifications] Fetching for user:", authUser?.username || authUser?.id);
      }
      const r = await apiCall.get("/admin-notifications?limit=50");
      setItems(r.data || []);
      lastFetchRef.current = Date.now();
      if (!silent) {
        console.log("[Notifications] Fetched", r.data?.length || 0, "notifications");
      }
    } catch (e) {
      if (!silent) {
        console.error("[Notifications] Fetch error:", e);
      }
      // Don't clear on error during polling - keep existing data
    }
  }, [authToken, authUser]);

  // Re-fetch when user changes (login/logout)
  useEffect(() => {
    fetchLatest();
  }, [fetchLatest, authUser?.id, authToken]);

  // Polling fallback - fetch every 30 seconds to catch notifications missed while PWA was closed
  useEffect(() => {
    if (!authToken) {
      // Clear interval if logged out
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    // Start polling
    pollIntervalRef.current = setInterval(() => {
      fetchLatest(true); // silent fetch
    }, POLL_INTERVAL);

    // Also fetch when app becomes visible (user returns to tab/PWA)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Only fetch if it's been more than 10 seconds since last fetch
        const timeSinceLastFetch = Date.now() - lastFetchRef.current;
        if (timeSinceLastFetch > 10000) {
          console.log("[Notifications] App became visible, refreshing...");
          fetchLatest(true);
        }
      }
    };

    // Also fetch when app comes back online
    const handleOnline = () => {
      console.log("[Notifications] Back online, refreshing...");
      fetchLatest(true);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [authToken, fetchLatest]);

  const addFromWs = useCallback((payload) => {
    if (!payload) return;
    setItems((prev) => {
      // avoid duplicates if reconnect replays
      const id = payload._id || payload.id;
      if (id && prev.some((p) => p._id === id || p.id === id)) return prev;
      const n = {
        _id: id,
        id: id,
        title: payload.title,
        body: payload.body,
        url: payload.url,
        createdAt: payload.createdAt || new Date().toISOString(),
        isRead: false,
        event: payload.event,
        type: payload.type || "booking", // support gift_card, system, etc.
        source: payload.source,
      };
      return [n, ...prev].slice(0, 200);
    });
  }, []);

  const markRead = useCallback(async (id) => {
    console.log("[Notifications] Marking as read:", id);
    setItems((prev) => prev.map((n) => n._id === id ? { ...n, isRead: true } : n));
    try {
      const result = await apiCall.post(`/admin-notifications/${id}/read`);
      console.log("[Notifications] Mark read result:", result.data);
    } catch (e) {
      console.error("[Notifications] Mark read error:", e);
      // rollback if needed
      setItems((prev) => prev.map((n) => n._id === id ? { ...n, isRead: false } : n));
    }
  }, []);

  const markAllRead = useCallback(async () => {
    console.log("[Notifications] Marking all as read");
    setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
    try {
      const result = await apiCall.post("/admin-notifications/read-all");
      console.log("[Notifications] Mark all read result:", result.data);
    } catch (e) {
      console.error("[Notifications] Mark all read error:", e);
    }
  }, []);

  const clearAll = useCallback(async () => {
    setItems([]);
    await apiCall.delete("/admin-notifications/clear");
  }, []);

  const value = useMemo(() => ({
    items,
    unreadCount: items.filter((i) => !i.isRead).length,
    addFromWs,
    fetchLatest,
    markRead,
    markAllRead,
    clearAll,
  }), [items, addFromWs, fetchLatest, markRead, markAllRead, clearAll]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
